// src/app/components/foundation/Card.tsx
import type { HTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Adds the hover-lift + glow interaction used across interactive cards. */
  interactive?: boolean;
  /**
   * One elevation step deeper at rest -- reserved for the single most
   * prominent surface on a given view (e.g. a page's hero header).
   */
  elevated?: boolean;
  /** Removes inner padding for cards that manage their own layout (tables, charts). */
  noPadding?: boolean;
  className?: string;
}

/**
 * Shared glass-morphism surface primitive for the representative-admin
 * foundation layer. Matches the existing VaniaLabs ui-engine glass style
 * (`.vania-glass-card` and its modifier classes): soft blur, hairline
 * border, low-opacity surface tint driven by theme tokens.
 *
 * This is the canonical implementation -- the two legacy `GlassCard`
 * components (`app/components/ui/GlassCard` and
 * `features/dashboard/components/ui/GlassCard`) now re-export this
 * component under their original name so every existing call site keeps
 * working unchanged.
 */
export function Card({
  children,
  interactive = false,
  elevated = false,
  noPadding = false,
  className,
  ...rest
}: CardProps) {
  return (
    <div
      className={clsx(
        'vania-glass-card',
        interactive && 'vania-glass-card--interactive',
        elevated && 'vania-glass-card--elevated',
        !noPadding && 'p-5 sm:p-6',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
